import React from 'react'
import { useApp } from '../context/AppContext'

// How long a toast stays up (review 2026-10-14: at least five seconds, longer
// when it offers something to do, like Undo).
export const TOAST_MS = 5000
export const TOAST_ACTION_MS = 8000

export function toastDuration(toast: { action?: unknown }): number {
  return toast.action ? TOAST_ACTION_MS : TOAST_MS
}

// Toast shows the latest message. The live regions are always in the page, so
// screen readers announce what appears in them: status for news, alert for
// errors. It waits while the pointer is over it or focus is in it.
export default function Toast() {
  const { state, dispatch } = useApp()
  const toast = state.toast
  const [paused, setPaused] = React.useState(false)

  React.useEffect(() => {
    setPaused(false)
  }, [toast?.id])

  React.useEffect(() => {
    if (!toast || paused) return
    const timer = setTimeout(() => dispatch({ type: 'CLEAR_TOAST', payload: toast.id }), toastDuration(toast))
    return () => clearTimeout(timer)
  }, [toast, paused, dispatch])

  const body = toast && (
    <div
      key={toast.id}
      className={`toast${toast.tone === 'error' ? ' toast--error' : ''}`}
      // A mouse only: a tap's emulated hover never "leaves", which would
      // keep a toast up for good on a phone.
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setPaused(true) }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setPaused(false) }}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast__message">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            toast.action?.run()
            dispatch({ type: 'CLEAR_TOAST', payload: toast.id })
          }}
        >
          {toast.action.label}
        </button>
      )}
    </div>
  )

  return (
    <>
      <div className="toast-region" role="status" aria-live="polite">
        {toast && toast.tone !== 'error' && body}
      </div>
      <div className="toast-region" role="alert">
        {toast && toast.tone === 'error' && body}
      </div>
    </>
  )
}
